import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Car as CarIcon, Pencil, Plus } from 'lucide-react';
import { CarFormModal } from '../components/forms/CarFormModal';
import { CarStatusBadge } from '../components/ui/Badge';
import { Button } from '../components/ui/Button';
import { Card, EmptyState, PageHeader } from '../components/ui/Card';
import { DataTable, type Column } from '../components/ui/DataTable';
import { FilterBar, SearchInput } from '../components/ui/FilterBar';
import { Select } from '../components/ui/Input';
import { useCars } from '../hooks/useApi';
import { toInt, useUrlFilters } from '../hooks/useUrlFilters';
import type { Car } from '../types';
import { CAR_STATUS_LABELS, FUEL_LABELS, formatNumber } from '../utils/helpers';
import { t } from '../i18n';

const statusFilters = () => [
  { value: 'active,service,sold', label: t('V evidencii (bez vyradených)') },
  { value: 'active', label: CAR_STATUS_LABELS.active },
  { value: 'service', label: CAR_STATUS_LABELS.service },
  { value: 'sold', label: CAR_STATUS_LABELS.sold },
  { value: 'retired', label: CAR_STATUS_LABELS.retired },
];

export function Cars() {
  const navigate = useNavigate();
  const [filters, setFilters] = useUrlFilters({ search: '', status: 'active,service,sold', page: '1', page_size: '20' });
  const [editing, setEditing] = useState<Car | null>(null);
  const [creating, setCreating] = useState(false);

  const page = toInt(filters.page, 1)!;
  const pageSize = toInt(filters.page_size, 20)!;
  const { data, isLoading, isFetching } = useCars({ search: filters.search, status: filters.status, page, page_size: pageSize });

  const columns: Column<Car>[] = [
    {
      key: 'plate',
      header: t('ŠPZ'),
      render: (car) => (
        <Link to={`/cars/${car.id}`} className="font-mono font-semibold text-primary-700 hover:underline" onClick={(e) => e.stopPropagation()}>
          {car.license_plate}
        </Link>
      ),
    },
    {
      key: 'vehicle',
      header: t('Vozidlo'),
      render: (car) => (
        <div>
          <p className="font-medium">
            {car.brand} {car.model}
          </p>
          {car.vin && <p className="font-mono text-xs text-secondary-500">{car.vin}</p>}
        </div>
      ),
    },
    { key: 'year', header: t('Rok'), render: (car) => car.year ?? '–' },
    { key: 'fuel', header: t('Palivo'), render: (car) => (car.fuel_type ? FUEL_LABELS[car.fuel_type] : '–') },
    { key: 'km', header: t('Stav km'), className: 'text-right tabular-nums', headerClassName: 'text-right', render: (car) => formatNumber(car.current_km) },
    { key: 'status', header: t('Stav'), render: (car) => <CarStatusBadge status={car.status} /> },
    {
      key: 'actions',
      header: <span className="sr-only">{t('Akcie')}</span>,
      className: 'text-right',
      render: (car) => (
        <button
          className="icon-btn"
          title={t('Upraviť')}
          aria-label={t('Upraviť {plate}', { plate: car.license_plate })}
          onClick={(e) => {
            e.stopPropagation();
            setEditing(car);
          }}
        >
          <Pencil className="h-4 w-4" />
        </button>
      ),
    },
  ];

  return (
    <>
      <PageHeader
        title={t('Vozidlá')}
        subtitle={t('Evidencia vozového parku')}
        actions={
          <Button onClick={() => setCreating(true)}>
            <Plus className="h-4 w-4" />  {t('Nové vozidlo')}
          </Button>
        }
      />
      <Card padding="none">
        <FilterBar>
          <SearchInput value={filters.search} onChange={(search) => setFilters({ search })} placeholder={t('Hľadať ŠPZ, značku, model, VIN…')} />
          <Select
            aria-label={t('Stav')}
            wrapperClassName="sm:w-64"
            value={filters.status}
            onChange={(e) => setFilters({ status: e.target.value })}
            options={statusFilters()}
            placeholder={t('Všetky stavy')}
          />
        </FilterBar>
        <DataTable
          data={data?.cars}
          columns={columns}
          rowKey={(car) => car.id}
          isLoading={isLoading}
          isFetching={isFetching}
          onRowClick={(car) => navigate(`/cars/${car.id}`)}
          emptyMessage={<EmptyState icon={<CarIcon className="h-10 w-10" />} title={t('Žiadne vozidlá nezodpovedajú filtru')} />}
          pagination={
            data && {
              page,
              pageSize,
              total: data.total,
              onPageChange: (p) => setFilters({ page: String(p) }),
              onPageSizeChange: (s) => setFilters({ page_size: String(s) }),
            }
          }
        />
      </Card>

      <CarFormModal isOpen={creating} onClose={() => setCreating(false)} onSaved={(car) => navigate(`/cars/${car.id}`)} />
      <CarFormModal isOpen={!!editing} car={editing} onClose={() => setEditing(null)} />
    </>
  );
}
